import React, { useCallback, useEffect, useState } from 'react';
import { Link } from '@hudl/uniform-ui-components';
import * as styles from './styles.scss';
import LausdLoginForm from './Lausd';
import LausdOrgLoginForm from './LausdOrg';
import LoginForm from './LoginForm';
import LoginHelp from './LoginHelp';
import CheckYourEmail from './CheckYourEmail';
import { LoginResponse } from 'requests/rest/login';
import { StartPasswordResetResponse } from 'requests/rest/startPasswordReset';
import g11n from 'hudl-g11n';
import PageBackground from 'components/shared/PageBackground';
import { Switch, Route, useHistory, useLocation } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useTitle } from 'hooks/useTitle';
import { usePrevious } from 'hooks/usePrevious';

interface LoginPageProps {
  forward?: string | null;
  initialError?: string;
  onLogin: (email: string, password: string, rememberMe: boolean) => Promise<LoginResponse>;
  onStartPasswordReset: (email: string) => Promise<StartPasswordResetResponse>;
}

const Page = {
  Login: '/login',
  Help: '/login/help',
  CheckEmail: '/login/check-email',
  Lausd: '/lausd/login',
  LausdOrg: '/lausd/login-org'
};
const getPageDepth = (page: string) => {
  switch (page) {
    case Page.Login:
      return 1;
    case Page.Help:
      return 2;
    case Page.CheckEmail:
      return 3;
    case Page.Lausd:
      return 4;
    case Page.LausdOrg:
      return 5;
    default:
      return -1;
  }
};

const LoginPage = (props: LoginPageProps) => {
  const { forward, initialError, onStartPasswordReset } = props;

  useTitle(g11n.format('login_login'));

  const [email, setEmail] = useState('');
  const [loginFormAnimation, setLoginFormAnimation] = useState<string | undefined>(styles.fadeInUp);
  const history = useHistory();
  const location = useLocation();

  const previousPage = usePrevious(location.pathname);

  const previousPageDepth = getPageDepth(previousPage);
  const currentPageDepth = getPageDepth(location.pathname);

  const slideDirection = previousPageDepth > currentPageDepth ? 'right' : 'left';

  // Route to the help page if #password-reset is the URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.toLowerCase() === '#password-reset') {
      setPage(Page.Help);
    }
    // Only want this to fire on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle page transitions
  const setPage = useCallback(
    (page: string) => {
      history.push(page);
    },
    [history],
  );

  const onBackButtonClick = useCallback(() => {
    if (location.pathname === Page.Login) {
      window.location.href = '/';
    } else if (location.pathname === Page.Help || location.pathname === Page.CheckEmail || location.pathname === Page.Lausd || location.pathname === Page.LausdOrg) {
      setPage(Page.Login);
    }
  }, [location, setPage]);

  const onPasswordResetClick = useCallback(
    async (passwordResetEmail: string) => {
      const response = await onStartPasswordReset(passwordResetEmail);

      if (response.success) {
        setPage(Page.CheckEmail);
      }

      return response;
    },
    [onStartPasswordReset, setPage],
  );

  const onLogin = useCallback(
    async (username: string, password: string, rememberMe: boolean) => {
      const result = await props.onLogin(username, password, rememberMe);

      if (result && result.success) {
        setLoginFormAnimation(styles.fadeOutDown);
      }

      return result;
    },
    [props],
  );

  // For LAUSD demo purposes
  let backButtonText = undefined;
  if (location.pathname === Page.Login) {
    backButtonText = undefined;
  } else if (location.pathname === Page.Lausd || location.pathname === Page.LausdOrg) {
    backButtonText = "lausd";
  } else {
    backButtonText = g11n.format('login_back');
  }
  const signUpText = location.pathname === Page.Lausd || location.pathname === Page.LausdOrg ? "" : g11n.format('login_sign_up')


  // const backButtonText = location.pathname === Page.Login ? undefined : g11n.format('login_back');

  return (
    <PageBackground environment="dark" backButton={{ text: backButtonText, onClick: onBackButtonClick }}>
      <Link href="/register/signup" className={styles.signUpLink}>
        {signUpText}
      </Link>

      <TransitionGroup
        component={null}
        // This child factory is necessary so that we can update the CSSTransition classNames before it starts exiting
        // Otherwise one element will get a 'right' class and the other will get a 'left' class and the animation gets weird
        childFactory={useCallback(child => React.cloneElement(child, { classNames: `slide-${slideDirection}` }), [
          slideDirection,
        ])}>
        <CSSTransition appear key={location.pathname} timeout={400}>
          <div className={styles.routeContainer}>
            <Switch location={location}>
              <Route exact path={Page.Login}>
                <LoginForm
                  forward={forward}
                  email={email}
                  initialError={initialError}
                  hudlLogoClassName={styles.fadeInUpFast}
                  formClassName={loginFormAnimation}
                  setEmail={setEmail}
                  onLogin={onLogin}
                  onHelpClick={useCallback(() => setPage('/login/help'), [setPage])}
                />
              </Route>
              <Route exact path={Page.Help}>
                <LoginHelp email={email} setEmail={setEmail} onPasswordReset={onPasswordResetClick} />
              </Route>
              <Route exact path={Page.CheckEmail}>
                <CheckYourEmail />
              </Route>
              <Route exact path={Page.Lausd}>
                <LausdLoginForm
                  forward={forward}
                  email={email}
                  initialError={initialError}
                  hudlLogoClassName={styles.fadeInUpFast}
                  formClassName={loginFormAnimation}
                  setEmail={setEmail}
                  onLogin={onLogin}
                  onHelpClick={useCallback(() => setPage('/login/help'), [setPage])}
                />
              </Route>
              <Route exact path={Page.LausdOrg}>
                <LausdOrgLoginForm
                  forward={forward}
                  email={email}
                  setEmail={setEmail}
                  onLogin={onLogin}
                  onHelpClick={useCallback(() => setPage('/login/help'), [setPage])}
                />
              </Route>
            </Switch>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </PageBackground>
  );
};

export default LoginPage;
